import { memo, useMemo } from 'react';

import { Typography, theme } from 'antd';

import { Mask } from 'v2/components/Shared/ValuesVisibility';

import { ELITE_LEVELS, ELITE_MAXIMUM_GAINS, type EliteLevel } from '../constants';

interface IProps {
  producerGains: number;
}

type BeltColorKey = 'white' | 'red' | 'orange' | 'green' | 'black' | 'golden';

const BELT_COLOR_BY_LEVEL: Record<string, BeltColorKey> = {
  '--red': 'red',
  '--orange': 'orange',
  '--green': 'green',
  '--black': 'black',
  '--golden': 'golden'
};

const mixBeltColors = (fromColor: string, toColor: string, toColorWeight: number) =>
  `color-mix(in srgb, ${fromColor} ${100 - toColorWeight}%, ${toColor} ${toColorWeight}%)`;

const TRACK_HEIGHT = 16;
const MARKER_WIDTH = 16;
const MARKER_HEIGHT = 19;
const TRACK_SEGMENTS = 10;
const TRACK_VIEWBOX_WIDTH = 1000;
const SEGMENT_WIDTH = TRACK_VIEWBOX_WIDTH / TRACK_SEGMENTS;
const TRACK_CLIP_PATH_ID = 'elite-progress-track-clip';

interface IProgressTrackProps {
  fromColor: string;
  toColor: string;
}

function ProgressTrack({ fromColor, toColor }: IProgressTrackProps) {
  return (
    <svg
      className='block h-4 w-full'
      viewBox={`0 0 ${TRACK_VIEWBOX_WIDTH} ${TRACK_HEIGHT}`}
      preserveAspectRatio='none'
      aria-hidden
    >
      <defs>
        <clipPath id={TRACK_CLIP_PATH_ID}>
          <rect width={TRACK_VIEWBOX_WIDTH} height={TRACK_HEIGHT} rx={TRACK_HEIGHT / 2} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${TRACK_CLIP_PATH_ID})`}>
        {Array.from({ length: TRACK_SEGMENTS }, (_, index) => {
          const colorWeight = Math.round(((index + 1) / TRACK_SEGMENTS) * 100);

          return (
            <rect
              key={index}
              x={index * SEGMENT_WIDTH}
              width={SEGMENT_WIDTH}
              height={TRACK_HEIGHT}
              fill={mixBeltColors(fromColor, toColor, colorWeight)}
            />
          );
        })}
      </g>
    </svg>
  );
}

interface IMarkerProps {
  progress: number;
  color: string;
  centerColor: string;
}

function ProgressMarker({ progress, color, centerColor }: IMarkerProps) {
  return (
    <svg className='absolute top-0 left-0 h-5 w-full overflow-visible' aria-hidden>
      <svg
        x={`${progress}%`}
        width={MARKER_WIDTH}
        height={MARKER_HEIGHT}
        viewBox={`0 0 ${MARKER_WIDTH} ${MARKER_HEIGHT}`}
        overflow='visible'
      >
        <g transform='translate(-8 0)'>
          <circle cx='8' cy='7' r='7' fill={color} />
          <path d='M3.2 11 8 18l4.8-7H3.2z' fill={color} />
          <circle cx='8' cy='7' r='2.25' fill={centerColor} />
        </g>
      </svg>
    </svg>
  );
}

const EliteProgressBar = memo(({ producerGains }: IProps) => {
  const { token } = theme.useToken();
  const gains = producerGains > 0 ? producerGains : 0;

  const beltColors = useMemo(
    () => ({
      white: token.colorBgContainer,
      red: token.colorError,
      orange: token.colorWarning,
      green: token.colorSuccess,
      black: token.colorText,
      golden: token.colorWarningActive
    }),
    [token]
  );

  const getLevels = useMemo<EliteLevel[]>(() => {
    if (gains >= ELITE_MAXIMUM_GAINS) {
      return [ELITE_LEVELS[ELITE_LEVELS.length - 2], ELITE_LEVELS[ELITE_LEVELS.length - 1]];
    }

    const secondLevel = ELITE_LEVELS.findIndex(item => gains < item.valueToAchieve);

    if (!secondLevel) {
      return [];
    }

    const firstLevel = secondLevel - 1;

    return [ELITE_LEVELS[firstLevel], ELITE_LEVELS[secondLevel]];
  }, [gains]);

  const progress = useMemo(() => {
    if (getLevels.length < 2) {
      return 0;
    }

    const [currentLevel, nextLevel] = getLevels;
    const range = nextLevel.valueToAchieve - currentLevel.valueToAchieve;
    const completed = gains - currentLevel.valueToAchieve;

    return Math.min(100, Math.max(0, (completed / range) * 100));
  }, [gains, getLevels]);

  const resolveBeltColorKey = (levelIndex: number, direction: 'backward' | 'forward'): BeltColorKey => {
    if (direction === 'backward') {
      for (let index = levelIndex; index >= 0; index -= 1) {
        const colorKey = BELT_COLOR_BY_LEVEL[ELITE_LEVELS[index].color];

        if (colorKey) {
          return colorKey;
        }
      }

      return 'white';
    }

    for (let index = levelIndex; index < ELITE_LEVELS.length; index += 1) {
      const colorKey = BELT_COLOR_BY_LEVEL[ELITE_LEVELS[index].color];

      if (colorKey) {
        return colorKey;
      }
    }

    return 'golden';
  };

  const [currentLevel, nextLevel] = getLevels;

  if (!currentLevel || !nextLevel) {
    return <ProgressTrack fromColor={token.colorFillSecondary} toColor={token.colorFillSecondary} />;
  }

  const currentLevelIndex = ELITE_LEVELS.findIndex(level => level.valueToAchieve === currentLevel.valueToAchieve);
  const nextLevelIndex = ELITE_LEVELS.findIndex(level => level.valueToAchieve === nextLevel.valueToAchieve);
  const fromColorKey = resolveBeltColorKey(currentLevelIndex, 'backward');
  const toColorKey = nextLevel.color
    ? (BELT_COLOR_BY_LEVEL[nextLevel.color] ?? resolveBeltColorKey(nextLevelIndex, 'forward'))
    : fromColorKey;
  const toColor = beltColors[toColorKey];
  const fromColor =
    fromColorKey === toColorKey ? mixBeltColors(token.colorBgContainer, toColor, 25) : beltColors[fromColorKey];

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex justify-between'>
        <Typography.Text className='text-base'>{currentLevel.name}</Typography.Text>
        <Typography.Text className='text-base'>{nextLevel.name}</Typography.Text>
      </div>

      <div
        className='relative pt-3'
        role='progressbar'
        aria-label='Progresso na faixa Elite'
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <ProgressMarker progress={progress} color={token.colorText} centerColor={token.colorTextSecondary} />

        <ProgressTrack fromColor={fromColor} toColor={toColor} />
      </div>

      <div className='flex justify-between'>
        <Typography.Text type='secondary'>
          <Mask>{currentLevel.label}</Mask>
        </Typography.Text>
        <Typography.Text type='secondary'>
          <Mask>{nextLevel.label}</Mask>
        </Typography.Text>
      </div>
    </div>
  );
});

export default EliteProgressBar;
